import { useState, useEffect, useRef } from 'react';
import { Socket } from 'socket.io-client';
import { socketClient } from '../services/api';
import { ChatMessage } from '../types';
import { useAuth } from '../context/AuthContext';

export function useSocket() {
  const { user, isAuthenticated } = useAuth();
  const socketRef = useRef<Socket | null>(socketClient.getSocket() as Socket | null);
  const [isConnected, setIsConnected] = useState(() => {
    const s = socketClient.getSocket();
    return s ? s.connected : false;
  });

  useEffect(() => {
    let currentSocket = socketClient.getSocket() as Socket | null;

    if (!currentSocket && isAuthenticated && user) {
      const token = localStorage.getItem('accessToken');
      if (token) {
        currentSocket = socketClient.connect(token) as Socket;
      }
    }

    if (currentSocket) {
      socketRef.current = currentSocket;

      const handleConnect = () => setIsConnected(true);
      const handleDisconnect = () => setIsConnected(false);

      if (currentSocket.connected) {
        setIsConnected(true);
      } else {
        setIsConnected(false);
      }

      currentSocket.on('connect', handleConnect);
      currentSocket.on('disconnect', handleDisconnect);

      return () => {
        if (currentSocket) {
          currentSocket.off('connect', handleConnect);
          currentSocket.off('disconnect', handleDisconnect);
        }
      };
    } else if (!isAuthenticated) {
      socketClient.disconnect();
      setIsConnected(false);
      socketRef.current = null;
    }
  }, [isAuthenticated, user]);

  return {
    socket: socketRef.current,
    isConnected,
    socketClient
  };
}

export function useRoom(roomId: string | null) {
  const { socket, socketClient } = useSocket();
  const [roomMembers, setRoomMembers] = useState<any[]>([]);
  const [isInRoom, setIsInRoom] = useState(false);

  useEffect(() => {
    if (!socket || !roomId) return;

    const handleRoomJoined = (data: any) => {
      setIsInRoom(true);
      setRoomMembers(data.members || []);
    };

    const handleRoomLeft = () => {
      setIsInRoom(false);
      setRoomMembers([]);
    };

    const handleUserJoined = (data: any) => {
      setRoomMembers(prev => [...prev, data]);
    };

    const handleUserLeft = (data: any) => {
      setRoomMembers(prev => prev.filter(member => member.userId !== data.userId));
    };

    socket.on('room:joined', handleRoomJoined);
    socket.on('room:left', handleRoomLeft);
    socket.on('room:userJoined', handleUserJoined);
    socket.on('room:userLeft', handleUserLeft);

    return () => {
      socket.off('room:joined', handleRoomJoined);
      socket.off('room:left', handleRoomLeft);
      socket.off('room:userJoined', handleUserJoined);
      socket.off('room:userLeft', handleUserLeft);
    };
  }, [socket, roomId]);

  const joinRoom = () => {
    if (roomId) {
      socketClient.joinRoom(roomId);
    }
  };

  const leaveRoom = () => {
    if (roomId) {
      socketClient.leaveRoom(roomId);
    }
  };

  return {
    roomMembers,
    isInRoom,
    joinRoom,
    leaveRoom
  };
}

export function useChat(roomId: string | null) {
  const { socket, socketClient } = useSocket();
  const { user } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [typingUsers, setTypingUsers] = useState<string[]>([]);

  useEffect(() => {
    if (!socket || !roomId) return;

    const handleMessageReceived = (message: ChatMessage) => {
      setMessages(prev => {
        // Remove temporary message if it exists and add the real one
        const withoutTemp = prev.filter(msg =>
          !msg.id.startsWith('temp-') ||
          !(msg.content === message.content && msg.userId === message.userId)
        );

        // Check if message already exists (avoid duplicates)
        const exists = withoutTemp.some(msg => msg.id === message.id);
        if (exists) return withoutTemp;

        return [...withoutTemp, message];
      });
    };

    const handleHistoryReceived = (data: { messages: ChatMessage[] }) => {
      setMessages(data.messages);
    };

    const handleTypingUpdate = (data: { typingUsers: string[] }) => {
      setTypingUsers(data.typingUsers);
    };

    const handleReactionUpdated = (data: any) => {
      setMessages(prev => prev.map(msg =>
        msg.id === data.messageId
          ? { ...msg, reactions: data.reactions }
          : msg
      ));
    };

    socket.on('chat:messageReceived', handleMessageReceived);
    socket.on('chat:historyReceived', handleHistoryReceived);
    socket.on('chat:typingUpdate', handleTypingUpdate);
    socket.on('chat:reactionUpdated', handleReactionUpdated);

    // Request history now (queued internally if socket not yet connected)
    socketClient.getChatHistory(roomId);

    // Re-request history on every reconnect (handles Render sleep/transport drops)
    const handleReconnect = () => {
      socketClient.getChatHistory(roomId);
    };
    socket.on('connect', handleReconnect);

    return () => {
      socket.off('chat:messageReceived', handleMessageReceived);
      socket.off('chat:historyReceived', handleHistoryReceived);
      socket.off('chat:typingUpdate', handleTypingUpdate);
      socket.off('chat:reactionUpdated', handleReactionUpdated);
      socket.off('connect', handleReconnect);
    };
  }, [socket, roomId, socketClient]);

  const sendMessage = (content: string, type = 'text', metadata?: any) => {
    if (roomId) {
      // Optimistic UI update - add message immediately
      const tempMessage: ChatMessage = {
        id: `temp-${Date.now()}`,
        roomId,
        userId: user?.id || '',
        username: user?.username || 'You',
        content,
        type: type as 'text' | 'file' | 'image' | 'system',
        timestamp: new Date().toISOString(),
        metadata
      };

      setMessages(prev => [...prev, tempMessage]);
      socketClient.sendMessage(roomId, content, type, metadata);
    }
  };

  const setTyping = (isTyping: boolean) => {
    if (roomId) {
      socketClient.setTyping(roomId, isTyping);
    }
  };

  const addReaction = (messageId: string, emoji: string) => {
    console.log('useChat addReaction called:', messageId, emoji);
    socketClient.addReaction(messageId, emoji);
  };

  const removeReaction = (messageId: string) => {
    console.log('useChat removeReaction called:', messageId);
    socketClient.removeReaction(messageId);
  };

  return {
    messages,
    typingUsers,
    sendMessage,
    setTyping,
    addReaction,
    removeReaction
  };
}

export function useVideoCall(roomId: string | null) {
  const { socket, socketClient } = useSocket();
  const [participants, setParticipants] = useState<any[]>([]);
  const [isInCall, setIsInCall] = useState(false);

  useEffect(() => {
    if (!socket || !roomId) return;

    const handleParticipantsUpdate = (data: { participants: any[] }) => {
      setParticipants(data.participants);
    };

    const handleParticipantJoined = (data: any) => {
      setParticipants(prev => [...prev, data.participant]);
    };

    const handleParticipantLeft = (data: any) => {
      setParticipants(prev => prev.filter(p => p.userId !== data.userId));
    };

    const handleMediaStateChanged = (data: any) => {
      setParticipants(prev => prev.map(p =>
        p.userId === data.userId
          ? { ...p, mediaState: data.mediaState }
          : p
      ));
    };

    socket.on('video:participantsUpdate', handleParticipantsUpdate);
    socket.on('video:participantJoined', handleParticipantJoined);
    socket.on('video:participantLeft', handleParticipantLeft);
    socket.on('video:mediaStateChanged', handleMediaStateChanged);

    return () => {
      socket.off('video:participantsUpdate', handleParticipantsUpdate);
      socket.off('video:participantJoined', handleParticipantJoined);
      socket.off('video:participantLeft', handleParticipantLeft);
      socket.off('video:mediaStateChanged', handleMediaStateChanged);
    };
  }, [socket, roomId]);

  const joinCall = (mediaState: any) => {
    if (roomId) {
      socketClient.joinVideoCall(roomId, mediaState);
      setIsInCall(true);
    }
  };

  const leaveCall = () => {
    if (roomId) {
      socketClient.leaveVideoCall(roomId);
      setIsInCall(false);
    }
  };

  const updateMediaState = (mediaState: any) => {
    if (roomId) {
      socketClient.updateMediaState(roomId, mediaState);
    }
  };

  return {
    participants,
    isInCall,
    joinCall,
    leaveCall,
    updateMediaState
  };
}

export function useWhiteboard(roomId: string | null) {
  const { socket, socketClient } = useSocket();
  const [whiteboardState, setWhiteboardState] = useState<any>(null);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    if (!socket || !roomId) return;

    const handleStateUpdate = (state: any) => {
      setWhiteboardState(state);
    };

    const handleUserJoined = () => {
      setIsActive(true);
    };

    const handleUserLeft = () => {
      // Keep active if other users are still present
    };

    const handleDrawingEvents = (data: any) => {
      // Handle real-time drawing updates
      setWhiteboardState((prev: any) => ({
        ...prev,
        lastUpdate: data
      }));
    };

    socket.on('whiteboard:stateUpdate', handleStateUpdate);
    socket.on('whiteboard:userJoined', handleUserJoined);
    socket.on('whiteboard:userLeft', handleUserLeft);
    socket.on('whiteboard:startDrawing', handleDrawingEvents);
    socket.on('whiteboard:drawing', handleDrawingEvents);
    socket.on('whiteboard:stopDrawing', handleDrawingEvents);

    return () => {
      socket.off('whiteboard:stateUpdate', handleStateUpdate);
      socket.off('whiteboard:userJoined', handleUserJoined);
      socket.off('whiteboard:userLeft', handleUserLeft);
      socket.off('whiteboard:startDrawing', handleDrawingEvents);
      socket.off('whiteboard:drawing', handleDrawingEvents);
      socket.off('whiteboard:stopDrawing', handleDrawingEvents);
    };
  }, [socket, roomId]);

  const joinWhiteboard = () => {
    if (roomId) {
      socketClient.joinWhiteboard(roomId);
    }
  };

  const leaveWhiteboard = () => {
    if (roomId) {
      socketClient.leaveWhiteboard(roomId);
      setIsActive(false);
    }
  };

  return {
    whiteboardState,
    isActive,
    joinWhiteboard,
    leaveWhiteboard,
    socketClient
  };
}
